import { z } from 'zod'
import { isValidTimeRange } from '@/lib/working-hours'
import type { Orgs } from '@/types/pbSchema'
import { DEFAULT_WORKING_HOURS } from '@/types/workingHours'

const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/

export const orgSettingsSchema = z
    .object({
        auto_approve_geo_entries: z.boolean(),
        period_type: z.enum(['weekly', 'biweekly_even', 'biweekly_odd', 'semi_monthly', 'monthly']),
        week_start_day: z.number().min(0).max(6),
        default_break_time_duration: z.number().min(0).max(480),
        track_travel_time: z.enum(['none', 'destination', 'source']),
        prefer_non_hq_job: z.boolean(),
        headquarters_job: z.string(),
        min_time_entry_duration: z.number().min(0).max(60),
        working_hours_enabled: z.boolean(),
        work_start_time: z.string().regex(TIME_REGEX),
        work_end_time: z.string().regex(TIME_REGEX),
        working_days: z.array(z.number().min(0).max(6)),
        timezone: z.string(),
        clockin_reminders_enabled: z.boolean(),
        clockin_grace_minutes: z.number().min(0).max(60),
        default_supervisors: z.array(z.string()),
    })
    .refine(
        data => {
            if (!data.working_hours_enabled) return true
            return isValidTimeRange(data.work_start_time, data.work_end_time)
        },
        {
            message: 'Start time must be before end time',
            path: ['work_end_time'],
        }
    )
    .refine(
        data => {
            if (!data.clockin_reminders_enabled) return true
            return data.timezone.length > 0
        },
        {
            message: 'Timezone is required when clock-in reminders are enabled',
            path: ['timezone'],
        }
    )
    .refine(data => !data.prefer_non_hq_job || data.headquarters_job.length > 0, {
        message: 'Set a headquarters job to prefer the non-HQ side',
        path: ['prefer_non_hq_job'],
    })
    .refine(data => !data.prefer_non_hq_job || data.track_travel_time !== 'none', {
        message: 'Travel tracking must be enabled to prefer the non-HQ side',
        path: ['prefer_non_hq_job'],
    })

export type OrgSettingsFormData = z.infer<typeof orgSettingsSchema>

const DEFAULT_FORM_VALUES: OrgSettingsFormData = {
    auto_approve_geo_entries: false,
    period_type: 'weekly',
    week_start_day: 1,
    default_break_time_duration: 30,
    track_travel_time: 'destination',
    prefer_non_hq_job: false,
    headquarters_job: '',
    min_time_entry_duration: 3,
    working_hours_enabled: DEFAULT_WORKING_HOURS.enabled,
    work_start_time: DEFAULT_WORKING_HOURS.startTime,
    work_end_time: DEFAULT_WORKING_HOURS.endTime,
    working_days: DEFAULT_WORKING_HOURS.workingDays,
    timezone: '',
    clockin_reminders_enabled: false,
    clockin_grace_minutes: 10,
    default_supervisors: [],
}

// Fields where a null or empty value in storage should fall back to the
// default rather than be carried into the form. Mostly enum/select fields
// that have no valid empty value, plus the working-hours times and days
// which are intentionally blanked out when working_hours_enabled is false.
const EMPTY_FALLBACK_KEYS = [
    'period_type',
    'track_travel_time',
    'timezone',
    'work_start_time',
    'work_end_time',
    'working_days',
    'working_hours_enabled',
    'default_supervisors',
] as const satisfies readonly (keyof OrgSettingsFormData)[]

function isEmptyValue(value: unknown): boolean {
    if (value == null) return true
    if (value === '') return true
    if (Array.isArray(value) && value.length === 0) return false
    return false
}

export function getDefaultFormValues(settings: Orgs | null): OrgSettingsFormData {
    if (!settings) return { ...DEFAULT_FORM_VALUES }
    // Object.assign skips own-properties with `undefined` values, so any
    // unset field on `settings` falls back to DEFAULT_FORM_VALUES.
    const merged = Object.assign({}, DEFAULT_FORM_VALUES, settings) as OrgSettingsFormData
    for (const key of EMPTY_FALLBACK_KEYS) {
        if (isEmptyValue(merged[key])) {
            // biome-ignore lint/suspicious/noExplicitAny: writing keyed by typed-defaults key
            ;(merged as any)[key] = DEFAULT_FORM_VALUES[key]
        }
    }
    return merged
}

export function buildUpdatePayload(data: OrgSettingsFormData): Partial<Orgs> {
    return {
        auto_approve_geo_entries: data.auto_approve_geo_entries,
        period_type: data.period_type,
        week_start_day: data.week_start_day,
        default_break_time_duration: data.default_break_time_duration,
        track_travel_time: data.track_travel_time,
        prefer_non_hq_job: data.prefer_non_hq_job,
        headquarters_job: data.headquarters_job || '',
        min_time_entry_duration: data.min_time_entry_duration,
        working_hours_enabled: data.working_hours_enabled,
        work_start_time: data.working_hours_enabled ? data.work_start_time : '',
        work_end_time: data.working_hours_enabled ? data.work_end_time : '',
        working_days: data.working_hours_enabled ? data.working_days : null,
        timezone: data.timezone,
        clockin_reminders_enabled: data.clockin_reminders_enabled,
        clockin_grace_minutes: data.clockin_grace_minutes,
        default_supervisors: data.clockin_reminders_enabled ? data.default_supervisors : [],
    }
}
